import { PidNode } from './PidNode'
import { cx, TagLabel, type PidBaseProps } from './shared'

export interface ValveProps extends PidBaseProps {
  open?: boolean
  /** 0–100 when this is a control / analog valve. Shown in the readout only. */
  position?: number
  /**
   * Flow-axis angle in degrees. Same convention as Pipe: 0 = right (horizontal),
   * 90 = down (vertical), clockwise. Rotates the glyph only — chrome stays upright.
   */
  degrees?: number
  /** Shorthand for `degrees={90}`. Ignored when `degrees` is set. */
  vertical?: boolean
  variant?: 'manual' | 'control'
  /** `port` for lattice diagrams; `stack` for gallery cards. */
  layout?: 'port' | 'stack'
}

const GLYPH_H = 40
const LEFT = 'M10 10 L28 20 L10 30 Z'
const RIGHT = 'M46 10 L28 20 L46 30 Z'
const STROKE = 'var(--foreground, #0a0a0a)'

export function Valve({
  open = true,
  position,
  degrees,
  vertical = false,
  variant = 'control',
  layout = 'port',
  tag,
  className,
  style,
  title
}: ValveProps) {
  const shown = position !== undefined ? Math.round(position) : open ? 100 : 0
  const angle = degrees ?? (vertical ? 90 : 0)

  const glyph = (
    <svg
      width={56}
      height={GLYPH_H}
      viewBox="0 0 56 40"
      aria-hidden
      style={angle !== 0 ? { transform: `rotate(${angle}deg)` } : undefined}
    >
      {variant === 'control' && (
        <>
          <circle cx={28} cy={6} r={6} fill="#fff" stroke={STROKE} strokeWidth={1.5} />
          <line x1={28} y1={12} x2={28} y2={20} stroke={STROKE} strokeWidth={1.5} />
        </>
      )}
      <path d={LEFT} fill="#fff" stroke={STROKE} strokeWidth={1.5} strokeLinejoin="round" />
      <path d={RIGHT} fill="#fff" stroke={STROKE} strokeWidth={1.5} strokeLinejoin="round" />
    </svg>
  )

  return (
    <div style={style} title={title ?? tag} className={cx(className)}>
      <PidNode glyph={glyph} glyphHeight={GLYPH_H} mode={layout}>
        {variant === 'control' && (
          <div className="pid-readout">
            <span className="pid-readout-value">{shown}</span>
            <span className="pid-readout-unit">%</span>
          </div>
        )}
        <TagLabel>{tag}</TagLabel>
      </PidNode>
    </div>
  )
}
